// Vendor imports
const classNames = require('classnames')
const _ = require('lodash')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
// Client V2 common imports
const { CircleButton } = require('client-v2/common/components/buttons')
const { composeRefs } = require('client-v2/common/components/utils')
const Icon = require('client-v2/common/containers/icons/component')
const useClientEffect = require('client-v2/common/hooks/client-effect')
const usePrevious = require('client-v2/common/hooks/previous')
const {
  useModalDepthModifier,
  useModalDepthDisableTouchScrollingBehindOverlay
 } = require('client-v2/common/contexts/modal-depth')
 const { keyCodesByName } = require('client-v2/common/utils/events')
 const tag = require('client-v2/common/utils/hyperscript')
// Container imports
const ArticleProfileDetailCard = require('../components/detail-card-smart')
const ArticleProfileRelated = require('../components/related-smart')
// Browser-only client V2 common imports
let mediaQueryManager
if (typeof window === 'object') {
  mediaQueryManager = require('client-v2/common/utils/media-query-manager')
}

// BEM Block entity definition
const bem = BemEntity('ArticleProfileModal')

// Swipe constants
const MAX_SWIPE_WIDTH_FACTOR = 0.9
const MIN_SWIPE_WIDTH_FACTOR = 0.25
const BROWSER_BACK_SWIPE_RANGE = 40 // px
const KEY_SCROLL_AMOUNT = 100 // px
const SwipeDirectionEnum = {
  NONE: 'none',
  LEFT: 'left',
  RIGHT: 'right'
}

// Keycode constants
const CLOSE_MODAL_KEYCODE = keyCodesByName.escape
const SWIPE_LEFT_KEYCODE = keyCodesByName.arrowLeft
const SWIPE_RIGHT_KEYCODE = keyCodesByName.arrowRight
const SCROLL_UP_KEYCODE = keyCodesByName.arrowUp
const SCROLL_DOWN_KEYCODE = keyCodesByName.arrowDown
const captureKeyCodes = [
  CLOSE_MODAL_KEYCODE,
  SWIPE_LEFT_KEYCODE,
  SWIPE_RIGHT_KEYCODE,
  SCROLL_UP_KEYCODE,
  SCROLL_DOWN_KEYCODE
]

const DEBOUNCE_DELAY = 300 // ms
const DEBOUNCE_OPTIONS = { leading: true, trailing: false }

function ArticleProfileModal(props) {
  const { className, currentIndex, modalDepth, slideCount, onClose } = props
  const prevProps = usePrevious(props)
  const currentSlideRef = React.useRef(null)
  const [ swipeDirection, setSwipeDirection ] = React.useState(SwipeDirectionEnum.NONE)
  const [ swipeWidth, setSwipeWidth ] = React.useState(0)

  const scrollCurrentSlide = React.useCallback(
    scrollAmount => {
      const slideEl = currentSlideRef.current
      if (slideEl && scrollAmount) {
        slideEl.scrollTop += scrollAmount
      }
    },
    [ currentSlideRef ]
  )

  // Swipe handling methods

  const showPreviousSlide = React.useMemo(
    () => _.debounce(props.onSwipeLeft, DEBOUNCE_DELAY, DEBOUNCE_OPTIONS),
    [ props.onSwipeLeft ]
  )
  const showNextSlide = React.useMemo(
    () => _.debounce(props.onSwipeRight, DEBOUNCE_DELAY, DEBOUNCE_OPTIONS),
    [ props.onSwipeRight ]
  )

  // Other event handling methods

  useModalDepthModifier()

  useClientEffect(
    () => {
      const hideLinks = document.querySelectorAll(`#root a, #root button`)
      hideLinks.forEach(link => {
        link.setAttribute('tabindex', '-1')
      })
      return () => {
        hideLinks.forEach(link => {
          link.setAttribute('tabindex', '0')
        })
      }
    },
    []
  )

  const stopBumpAnimation = useBumpAnimation({
    isNewlyVisible: !currentSlideRef,
    setOffset: setSwipeWidth
  })

  usePropsChangedSideEffects({
    prevProps,
    props,
    setSwipeDirection
  })

  useKeyDownListener({
    closeModal: props.onClose,
    scrollCurrentSlide,
    showPreviousSlide,
    showNextSlide,
    stopBumpAnimation
  })

  const isSwiping = useTouchSwipeListener({
    currentSlideRef,
    showPreviousSlide,
    showNextSlide,
    stopBumpAnimation,
    setSwipeDirection,
    setSwipeWidth
  })

  // Render
  return (
    tag('div', {
      className: classNames(
        className,
        bem.block()
      )
    },
      tag(ArticleProfileModalBackdrop, {
        onClick: onClose
      }),
      tag(ArticleProfileModalContent, {
        currentIndex,
        currentSlideRef,
        isSwiping,
        modalDepth,
        slideCount,
        swipeDirection,
        swipeWidth,
        onClose
      }),
      tag(ArticleProfileModalCloseButton, {
        onClick: onClose
      }),
      tag(ArticleProfileModalSwipeButtons, {
        currentIndex,
        slideCount,
        showPreviousSlide,
        showNextSlide
      })
    )
  )
}

// Bump animation hook

const bumpAnimationKeyframes = (() => {
  const initialDelay = 2000
  const duration = 500
  const bumpOffset = -50
  return _.range(0, 4).map(i => ({
    delay: initialDelay + (i * duration),
    offset: i % 2 ? 0 : bumpOffset
  }))
})()

function useBumpAnimation({ isNewlyVisible, setOffset }) {
  const playingRef = React.useRef(false)
  const timersRef = React.useRef([])

  const startBumpAnimation = React.useCallback(
    () => {
      timersRef.current = bumpAnimationKeyframes.map(keyframe => (
        setTimeout(() => {
          setOffset(keyframe.offset)
        }, keyframe.delay)
      ))
      playingRef.current = true
    },
    [ setOffset ]
  )

  const stopBumpAnimation = React.useCallback(
    () => {
      if (!playingRef.current) {
        return
      }
      timersRef.current.forEach(clearTimeout)
      playingRef.current = false
      setOffset(0)
    },
    [ setOffset ]
  )

  useClientEffect(
    () => {
      if (isNewlyVisible && mediaQueryManager.matches('small-and-down')) {
        startBumpAnimation()
      }
    },
    [ isNewlyVisible, startBumpAnimation ]
  )

  return stopBumpAnimation
}

function usePropsChangedSideEffects({
  prevProps,
  props,
  setSwipeDirection
}) {
  const hasPrevProps = !!prevProps
  const {
    currentIndex: prevIndex,
    modalDepth: prevDepth,
    slideCount: prevCount
  } = prevProps || {}
  const {
    currentIndex,
    modalDepth,
    slideCount,
    onClose: closeModal,
    onSlideRefresh: refreshCurrentSlide
  } = props
  React.useEffect(
    () => {
      if (!hasPrevProps) {
        return
      }
      const indexDiff = prevIndex - currentIndex
      const depthDiff = prevDepth - modalDepth
      const countDiff = prevCount - slideCount

      if (!slideCount) {
        // Close the modal if there are no slides to show
        closeModal()
      } else if (countDiff >= 1 && !depthDiff) {
        // Refresh the current slide if slides have been removed
        refreshCurrentSlide()
      } else if (Math.abs(indexDiff) > 1 || depthDiff) {
        // Prevent sliding behavior when showing related article profile modal
        setSwipeDirection(SwipeDirectionEnum.NONE)
      } else if (indexDiff > 0) {
        setSwipeDirection(SwipeDirectionEnum.LEFT)
      } else if (indexDiff < 0) {
        setSwipeDirection(SwipeDirectionEnum.RIGHT)
      }

      // Hide other slides for accessibility improvements
      const hideLinks = document.querySelectorAll(`
        .ArticleProfileModal__slide-outer--next a, .ArticleProfileModal__slide-outer--next button,
        .ArticleProfileModal__slide-outer--previous a, .ArticleProfileModal__slide-outer--previous button
      `)
      hideLinks.forEach(link => {
        link.setAttribute('tabindex', '-1')
      })
      return () => {
        hideLinks.forEach(link => {
          link.setAttribute('tabindex', '0')
        })
      }
    },
    [
      currentIndex, hasPrevProps, modalDepth, prevCount, prevDepth, prevIndex,
      closeModal, refreshCurrentSlide, setSwipeDirection, slideCount
    ]
  )
}

function shouldIgnoreKeyDownEvent({ keyCode, target: { tagName } }) {
  // Ignore keyCodes that the modal is not interested in
  if (!captureKeyCodes.includes(keyCode)) {
    return true
  }
  // Ensure text-capturing inputs keep doing their thing
  if (tagName === 'INPUT' || tagName === 'TEXTAREA') {
    return true
  }
  return false
}

function useKeyDownListener({
  closeModal,
  scrollCurrentSlide,
  showNextSlide,
  showPreviousSlide,
  stopBumpAnimation
}) {
  const onKeyDown = React.useCallback(
    event => {
      if (shouldIgnoreKeyDownEvent(event)) {
        return
      }

      event.preventDefault()
      stopBumpAnimation()

      switch (event.keyCode) {
        case CLOSE_MODAL_KEYCODE:
          stopBumpAnimation()
          closeModal()
          break
        case SWIPE_LEFT_KEYCODE:
          stopBumpAnimation()
          showPreviousSlide()
          break
        case SWIPE_RIGHT_KEYCODE:
          stopBumpAnimation()
          showNextSlide()
          break
        case SCROLL_UP_KEYCODE:
          scrollCurrentSlide(-KEY_SCROLL_AMOUNT)
          break
        case SCROLL_DOWN_KEYCODE:
          scrollCurrentSlide(KEY_SCROLL_AMOUNT)
          break
      }
    },
    [ closeModal, scrollCurrentSlide, stopBumpAnimation, showPreviousSlide, showNextSlide ]
  )
  useClientEffect(
    () => {
      window.addEventListener('keydown', onKeyDown)
      return () => {
        window.removeEventListener('keydown', onKeyDown)
      }
    },
    [ onKeyDown ]
  )
}

function useTouchSwipeListener({
  currentSlideRef,
  showNextSlide,
  showPreviousSlide,
  setSwipeDirection,
  setSwipeWidth,
  stopBumpAnimation
}) {
  const swipeRef = React.useRef(null)
  const [ isSwiping, setSwiping ] = React.useState(false)

  const onSwipeStart = useTouchSwipeStartCallback({
    currentSlideRef,
    setSwipeDirection,
    setSwipeWidth,
    setSwiping,
    stopBumpAnimation,
    swipeRef
  })

  const onSwipeMove = useTouchSwipeMoveCallback({
    setSwipeWidth,
    swipeRef
  })

  const onSwipeEnd = useTouchSwipeEndCallback({
    setSwipeWidth,
    setSwiping,
    showNextSlide,
    showPreviousSlide,
    swipeRef
  })

  useClientEffect(
    () => {
      document.addEventListener('touchstart', onSwipeStart)
      document.addEventListener('touchmove', onSwipeMove)
      document.addEventListener('touchend', onSwipeEnd)
      return () => {
        document.removeEventListener('touchstart', onSwipeStart)
        document.removeEventListener('touchmove', onSwipeMove)
        document.removeEventListener('touchend', onSwipeEnd)
      }
    },
    [ onSwipeEnd, onSwipeMove, onSwipeStart ]
  )

  return isSwiping
}

function useTouchSwipeStartCallback({
  currentSlideRef,
  setSwipeDirection,
  setSwipeWidth,
  setSwiping,
  stopBumpAnimation,
  swipeRef
}) {
  return React.useCallback(
    event => {
      const currentSlideEl = currentSlideRef.current
      const swipe = swipeRef.current
      if (!currentSlideEl || swipe) {
        return
      }
      const slideRect = currentSlideEl.getBoundingClientRect()

      swipeRef.current = {
        maxWidth: slideRect.width * MAX_SWIPE_WIDTH_FACTOR,
        touchStartX: _.get(event, 'touches[0].pageX'),
        touchStartY: _.get(event, 'touches[0].pageY'),
        width: 0
      }
      stopBumpAnimation()
      setSwiping(true)
      setSwipeDirection(SwipeDirectionEnum.NONE)
      setSwipeWidth(0)
    },
    [ currentSlideRef, setSwipeDirection, setSwipeWidth, setSwiping, stopBumpAnimation, swipeRef ]
  )
}

function useTouchSwipeMoveCallback({
  setSwipeWidth,
  swipeRef
}) {
  return React.useCallback(
    event => {
      const swipe = swipeRef.current
      if (!swipe) {
        return
      }
      // Determine the delta of the movement
      const moveX = _.get(event, 'touches[0].pageX')
      const moveY = _.get(event, 'touches[0].pageY')
      const diffX = swipe.touchStartX - moveX
      const diffY = swipe.touchStartY - moveY
      // Cancel the swipe if there's a more-important gesture
      const isVerticalSwipe = Math.abs(diffY) > Math.abs(diffX)
      const isBrowserBackSwipe = swipe.touchStartX < BROWSER_BACK_SWIPE_RANGE
      if (isVerticalSwipe || isBrowserBackSwipe) {
        swipe.width = 0
        setSwipeWidth(0)
        return
      }
      // Determine which slide to swipe in and how far
      let swipeWidth = 0
      if (diffX < 0) {
        swipeWidth = Math.min(-diffX, swipe.maxWidth)
      } else if (diffX > 0) {
        swipeWidth = Math.max(-diffX, -swipe.maxWidth)
      }
      // Peek the next/previous slide in by the swipe amount
      swipe.width = swipeWidth
      setSwipeWidth(swipeWidth)
    },
    [ setSwipeWidth, swipeRef ]
  )
}

function useTouchSwipeEndCallback({
  setSwipeWidth,
  setSwiping,
  showNextSlide,
  showPreviousSlide,
  swipeRef
}) {
  return React.useCallback(
    () => {
      const swipe = swipeRef.current
      if (!swipe) {
        return
      }
      const threshold = swipe.maxWidth * MIN_SWIPE_WIDTH_FACTOR
      const swipeWidth = swipe.width
      // Clear swipe state
      swipeRef.current = null
      setSwiping(false)
      setSwipeWidth(0)

      // Determine if swipe triggered index update
      if (Math.abs(swipeWidth) > threshold) {
        if (swipeWidth > 0) {
          showPreviousSlide()
        }
        if (swipeWidth < 0) {
          showNextSlide()
        }
      }
    },
    [ setSwipeWidth, setSwiping, showPreviousSlide, showNextSlide, swipeRef ]
  )
}

// BEM Block entity Element components

function ArticleProfileModalBackdrop({
  onClick
}) {
  return (
    tag('div', {
      className: bem.element('backdrop'),
      onClick
    })
  )
}

function ArticleProfileModalCloseButton({
  onClick
}) {
  return (
    tag('div', {
      className: bem.element('close-container')
    },
      tag(CircleButton, {
        'aria-label': 'Close dialog',
        className: bem.element('close-button'),
        type: 'button',
        onClick
      })
    )
  )
}

function ArticleProfileModalSwipeButtons({
  currentIndex,
  slideCount,
  showPreviousSlide,
  showNextSlide
}) {
  return (
    tag('div', {
      className: bem.element('buttons-container')
    },
      tag(ArticleProfileModalSwipeLeftButton, {
        isVisible: currentIndex > 0,
        onClick: showPreviousSlide
      }),
      tag(ArticleProfileModalSwipeRightButton, {
        isVisible: currentIndex + 1 < slideCount,
        onClick: showNextSlide
      })
    )
  )
}

function ArticleProfileModalSwipeLeftButton({
  isVisible,
  onClick
}) {
  if (!isVisible) {
    return null
  }
  return (
    tag(CircleButton, {
      'aria-label': 'Swipe to previous slide',
      className: bem.element('swipe-button', 'left'),
      onClick
    },
      tag(Icon, { id: 'previous' })
    )
  )
}

function ArticleProfileModalSwipeRightButton({
  isVisible,
  onClick
}) {
  if (!isVisible) {
    return null
  }
  return (
    tag(CircleButton, {
      'aria-label': 'Swipe to next slide',
      className: bem.element('swipe-button', 'right'),
      onClick
    },
      tag(Icon, { id: 'next' })
    )
  )
}

function ArticleProfileModalContent({
  currentIndex,
  currentSlideRef,
  isSwiping,
  modalDepth,
  slideCount,
  swipeDirection,
  swipeWidth,
  onClose
}) {
  const transform = `translateX(${swipeWidth}px)`

  const onCloseCapture = React.useCallback(
    event => {
      const captureClassName = bem.element('slide-outer')
      if (event.target.classList.contains(captureClassName)) {
        event.preventDefault()
        event.stopPropagation()
        onClose()
      }
    },
    [ onClose ]
  )

  const renderSlide = slideIndexOffset => (
    tag(ArticleProfileModalSlide, {
      // There is no unique key; falling back to index since these don't re-order.
      // Including modal depth so that going deeper forces a re-render.
      key: `${modalDepth}:${currentIndex + slideIndexOffset}`,
      currentIndex,
      currentSlideRef,
      slideCount,
      slideIndexOffset,
      onCloseCapture
    })
  )

  return (
    tag('div', {
      className: bem.element('content', {
        swiping: isSwiping,
        direction: swipeDirection
      }),
      style: { transform }
    },
      // Use an array with keys to better-control component re-use/re-rendering
      [
        renderSlide(-1),
        renderSlide(0),
        renderSlide(+1)
      ]
    )
  )
}

function ArticleProfileModalSlide({
  currentIndex,
  currentSlideRef,
  slideCount,
  slideIndexOffset,
  onCloseCapture
}) {
  const slideIndex = currentIndex + slideIndexOffset
  const portalContainerRef = React.useRef()
  const scrollableRef = React.useRef()
  const slideOuterRef = composeRefs(
    slideIndexOffset ? null : currentSlideRef,
    scrollableRef
  )

  useModalDepthDisableTouchScrollingBehindOverlay(scrollableRef)

  if (slideIndex < 0 || slideIndex >= slideCount) {
    portalContainerRef.current = null
    scrollableRef.current = null
    return null
  }

  return (
    tag('div', {
      ref: slideOuterRef,
      // Outer is responsible for the full-window scrolling
      className: bem.element('slide-outer', {
        previous: slideIndexOffset < 0,
        current: slideIndexOffset == 0,
        next: slideIndexOffset > 0
      }),
      onClickCapture: onCloseCapture
    },
      tag('div', {
        // Inner is responsible for centered content
        className: bem.element('slide-inner')
      },
        tag('div', {
          ref: portalContainerRef,
          className: bem.element('detail-card-container')
        },
          tag(ArticleProfileDetailCard, {
            articleMixButtonPortalContainer: portalContainerRef,
            slideIndex
          }),
          tag(ArticleProfileRelated, {
            articleMixButtonPortalContainer: portalContainerRef,
            slideIndex
          })
        )
      )
    )
  )
}

if (__DEV__) {
  // Prop checking imports
  const PropTypes = require('prop-types')

  ArticleProfileModal.propTypes = {
    // Values
    className: PropTypes.string,
    currentIndex: PropTypes.number.isRequired,
    modalDepth: PropTypes.number.isRequired,
    slideCount: PropTypes.number.isRequired,
    // Callbacks
    onClose: PropTypes.func.isRequired,
    onSlideRefresh: PropTypes.func.isRequired,
    onSwipeLeft: PropTypes.func.isRequired,
    onSwipeRight: PropTypes.func.isRequired
  }
}

module.exports = ArticleProfileModal
